import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import { SoundRecordingAudit } from "../../../utils/@types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "../../YouTubeScorecard/Components/Tables/tables.css";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";
import { withExportDate } from "../../../utils/exportFileName";

interface ColumnConfig {
  field: string;
  header: string;
  sortable?: boolean;
  visible: boolean;
}

const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
  onColumnVisibility,
}: any) => {
  const brandOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.brand))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const channelManagerOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.ChannelManagerName))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntity))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntityType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem" }}>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.videoId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, videoId: items }))
            }
            label={"Video ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Brand
          </text>
          <MultiSelect
            value={filters.brand}
            options={brandOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, brand: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Channel Manager
          </text>
          <MultiSelect
            value={
              filters.ChannelManagerName ? [filters.ChannelManagerName] : []
            }
            options={channelManagerOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                ChannelManagerName: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity
          </text>
          <MultiSelect
            value={filters.legalEntity ? [filters.legalEntity] : []}
            options={legalEntityOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntity: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity Type
          </text>
          <MultiSelect
            value={filters.legalEntityType ? [filters.legalEntityType] : []}
            options={legalEntityTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntityType: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <div
            className="flex align-items-center gap-2"
            style={{ marginTop: "1.5rem" }}
          >
            <Checkbox
              checked={filters.highPriority || false}
              onChange={(e) =>
                setFilters((prev: any) => ({
                  ...prev,
                  highPriority: e.checked || false,
                }))
              }
            />
            <label className="text-sm font-medium" style={{ color: "grey" }}>
              High Priority Only
            </label>
          </div>
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Columns"
              icon="pi pi-columns"
              onClick={onColumnVisibility}
              severity="secondary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const MismatchedSoundRecordingsAudit: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      brand: parseStringArrayParam(params, "brand"),
      ChannelManagerName: parseStringParam(params, "ChannelManagerName"),
      legalEntity: parseStringParam(params, "legalEntity"),
      legalEntityType: parseStringParam(params, "legalEntityType"),
      highPriority: parseBooleanParam(params, "highPriority") || false,
    };
  }, [location.search]);

  const dt = useRef<DataTable<SoundRecordingAudit[]>>(null);
  const toast = useRef<Toast>(null);
  const initialFiltersApplied = useRef(false);
  const [rows, setRows] = useState<SoundRecordingAudit[]>([]);
  const [filters, setFilters] = useState<any>(initialFilters);
  const [unfilteredData, setUnfilteredData] = useState<SoundRecordingAudit[]>(
    []
  );
  const [filteredRows, setFilteredRows] = useState<SoundRecordingAudit[]>([]);
  const [columnVisibilityDialog, setColumnVisibilityDialog] = useState(false);

  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { field: "isHighPriority", header: "Priority", visible: true },
    { field: "videoId", header: "Video ID", sortable: true, visible: true },
    {
      field: "videoTitle",
      header: "Video Title",
      sortable: true,
      visible: true,
    },
    {
      field: "videoType",
      header: "Video type",
      sortable: true,
      visible: true,
    },
    {
      field: "soundRecordingId",
      header: "Sound Recording ID",
      sortable: true,
      visible: true,
    },
    {
      field: "expectedSoundRecordingId",
      header: "Expected SR ID",
      sortable: true,
      visible: true,
    },
    {
      field: "assetCode",
      header: "Asset Code",
      sortable: true,
      visible: true,
    },
    {
      field: "pointOfFailure",
      header: "Point of Failure",
      sortable: true,
      visible: true,
    },
    {
      field: "approxDailyViews",
      header: "Approx Daily Views",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntity",
      header: "Legal Entity",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntityType",
      header: "Legal Entity Type",
      sortable: true,
      visible: true,
    },
    { field: "checked", header: "Checked", visible: true },
    { field: "notes", header: "Notes", visible: true },
  ]);

  useEffect(() => {
    apiService.musicAssetAudit
      .getMismatchedSoundRecordings({})
      .then((data: any) => {
        setUnfilteredData(data);
        setRows(data);
        setFilteredRows(data);
      });
  }, []);

  const isHighPriorityRow = (row: SoundRecordingAudit): boolean =>
    row.isHighPriority || false;

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    let filtered = rows;
    if (filters.videoId?.length)
      filtered = filtered.filter((r) =>
        filters.videoId.some((v: string) =>
          r.videoId?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.brand?.length)
      filtered = filtered.filter((r) => filters.brand.includes(r.brand));
    if (filters.ChannelManagerName)
      filtered = filtered.filter(
        (r) => r.ChannelManagerName === filters.ChannelManagerName
      );
    if (filters.highPriority)
      filtered = filtered.filter((r) => r.isHighPriority === true);
    if (filters.legalEntity)
      filtered = filtered.filter(
        (r) => r.LegalEntity === filters.legalEntity
      );
    if (filters.legalEntityType)
      filtered = filtered.filter(
        (r) => r.LegalEntityType === filters.legalEntityType
      );
    setFilteredRows(filtered);
    const query = buildQueryString({
      videoId: filters.videoId,
      brand: filters.brand,
      ChannelManagerName: filters.ChannelManagerName,
      legalEntity: filters.legalEntity,
      legalEntityType: filters.legalEntityType,
      highPriority: filters.highPriority,
    });
    navigate({ search: query }, { replace: true });
  }, [filters, navigate, rows]);

  const handleReset = () => {
    setFilters({
      videoId: [],
      brand: [],
      ChannelManagerName: "",
      highPriority: false,
      legalEntity: "",
      legalEntityType: "",
    });
    setFilteredRows(rows);
    navigate({ search: "" }, { replace: true });
  };

  useEffect(() => {
    const hasInitialFilters = Object.values(initialFilters).some((value) => {
      if (typeof value === "boolean") return value;
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    if (!hasInitialFilters || rows.length === 0 || initialFiltersApplied.current)
      return;
    handleSearch();
    initialFiltersApplied.current = true;
  }, [initialFilters, rows, handleSearch]);

  const onRowEditComplete = (e: any) => {
    let _rows = [...rows];
    let { newData, index } = e;
    _rows[index] = newData;
    setRows(_rows);
    apiService.musicAssetAudit.updateMissingSoundRecording(newData);
  };

  const checkedBodyTemplate = (rowData: SoundRecordingAudit) => (
    <Checkbox
      onChange={(e) => {
        let _rows = [...rows];
        const index = _rows.findIndex((r) => r.videoId === rowData.videoId);
        _rows[index].checked = e.checked;
        setRows(_rows);
        apiService.musicAssetAudit.updateMissingSoundRecording({
          videoId: rowData.videoId,
          checked: e.checked,
        });
      }}
      checked={rowData.checked ?? false}
    />
  );

  const notesEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback && options.editorCallback(e.target.value)
      }
    />
  );

  const rowClassName = (data: SoundRecordingAudit) => ({
    "bg-blue-100": data.isNew,
    "bg-red-100 border-l-4 border-red-500": isHighPriorityRow(data),
  });

  const priorityBodyTemplate = (rowData: SoundRecordingAudit) => {
    const isHighPriority = isHighPriorityRow(rowData);
    const getPriorityText = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Views",
          priority_channel: "Priority Channel",
          new_asset_code: "New Asset Code",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };
    const getPriorityTooltip = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Daily Views (>25,000)",
          priority_channel: "Priority Channel (Uploaded in last 30 days)",
          new_asset_code: "New Asset Code (Uploaded in last 14 days)",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    return (
      <div className="flex align-items-center gap-2">
        {rowData.isPriority && (
          <span
            className="text-yellow-600 font-semibold text-xs"
            title="Manual Priority"
          >
            Manual
          </span>
        )}
        {isHighPriority && (
          <span
            className="text-red-600 font-semibold text-xs px-2 py-1 bg-red-100 rounded"
            title={getPriorityTooltip()}
          >
            {getPriorityText()}
          </span>
        )}
      </div>
    );
  };

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible } : col))
    );
  };

  const resetColumnVisibility = () => {
    setColumnConfig((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const isColumnVisible = (field: string): boolean => {
    const column = columnConfig.find((col) => col.field === field);
    return column?.visible === true || false;
  };

  const calculateSummaryMetrics = () => {
    const totalIssues = filteredRows.length;
    const uniqueAssetCodes = new Set(filteredRows.map((row) => row.assetCode))
      .size;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const newIssues = filteredRows.filter((row) => {
      if (!row.timeUploaded) return false;
      return new Date(row.timeUploaded) >= sevenDaysAgo;
    }).length;

    const totalApproxDailyViews = filteredRows.reduce(
      (sum, row) => sum + (row.approxDailyViews || 0),
      0
    );

    return { totalIssues, uniqueAssetCodes, newIssues, totalApproxDailyViews };
  };

  const summaryMetrics = calculateSummaryMetrics();

  const handleCellClick = (event: any) => {
    const cellValue = event.value;
    if (cellValue && String(cellValue).trim()) {
      const valueToCopy = String(cellValue).trim();
      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => {
          toast.current?.show({
            severity: "success",
            summary: "Copied!",
            detail: `"${valueToCopy}" copied to clipboard`,
            life: 2000,
          });
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 2000,
          });
        });
    }
  };

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <Toast ref={toast} />
      <Dialog
        header="Column Visibility"
        visible={columnVisibilityDialog}
        style={{ width: "40vw" }}
        onHide={() => setColumnVisibilityDialog(false)}
      >
        <div className="grid">
          {columnConfig.map((column) => (
            <div key={column.field} className="col-12 md:col-6 lg:col-4">
              <div className="flex align-items-center gap-2 p-2">
                <Checkbox
                  checked={column.visible}
                  onChange={(e) =>
                    handleColumnVisibilityChange(
                      column.field,
                      e.checked || false
                    )
                  }
                />
                <label className="text-sm">{column.header}</label>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Reset All"
            icon="pi pi-refresh"
            onClick={resetColumnVisibility}
            severity="secondary"
            outlined
          />
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setColumnVisibilityDialog(false)}
          />
        </div>
      </Dialog>

      <Card
        className="mb-3"
        style={{
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-blue-600">
                {summaryMetrics.totalIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Mismatches</div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-green-600">
                {summaryMetrics.uniqueAssetCodes.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Unique Asset Codes</div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-orange-600">
                {summaryMetrics.newIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">
                New Mismatches (7 days)
              </div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-purple-600">
                {summaryMetrics.totalApproxDailyViews.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Daily Views</div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
          onColumnVisibility={() => setColumnVisibilityDialog(true)}
        />
        <DataTable
          ref={dt}
          exportFilename={withExportDate("Sound Recording Mismatch Audit")}
          value={filteredRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          onCellClick={handleCellClick}
          rowClassName={rowClassName}
        >
          {isColumnVisible("videoId") && (
            <Column field="videoId" header="Video ID" sortable />
          )}
          {isColumnVisible("videoTitle") && (
            <Column field="videoTitle" header="Video Title" sortable />
          )}
          {isColumnVisible("videoType") && (
            <Column field="videoType" header="Video type" sortable />
          )}
          {isColumnVisible("soundRecordingId") && (
            <Column
              field="soundRecordingId"
              header="Sound Recording ID"
              sortable
            />
          )}
          {isColumnVisible("expectedSoundRecordingId") && (
            <Column
              field="expectedSoundRecordingId"
              header="Expected SR ID"
              sortable
            />
          )}
          {isColumnVisible("assetCode") && (
            <Column field="assetCode" header="Asset Code" sortable />
          )}
          {isColumnVisible("pointOfFailure") && (
            <Column field="pointOfFailure" header="Point of Failure" sortable />
          )}
          {isColumnVisible("approxDailyViews") && (
            <Column
              field="approxDailyViews"
              header="Approx Daily Views"
              body={(rowData) =>
                rowData.approxDailyViews?.toLocaleString() || "0"
              }
              sortable
            />
          )}
          {isColumnVisible("LegalEntity") && (
            <Column
              field="LegalEntity"
              header="Legal Entity"
              sortable
            />
          )}
          {isColumnVisible("LegalEntityType") && (
            <Column
              field="LegalEntityType"
              header="Legal Entity Type"
              sortable
            />
          )}
          {isColumnVisible("isHighPriority") && (
            <Column
              body={priorityBodyTemplate}
              header="Priority"
              sortable
              field="isHighPriority"
            />
          )}
          {isColumnVisible("LegalEntity") && (
            <Column
              field="LegalEntity"
              header="Legal Entity"
              sortable
            />
          )}
          {isColumnVisible("LegalEntityType") && (
            <Column
              field="LegalEntityType"
              header="Legal Entity Type"
              sortable
            />
          )}
          <Column
            rowEditor
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          {isColumnVisible("checked") && (
            <Column body={checkedBodyTemplate} header="Checked" />
          )}
          {isColumnVisible("notes") && (
            <Column
              field="notes"
              header="Notes"
              editor={notesEditor}
              sortable
            />
          )}
        </DataTable>
      </Card>
    </div>
  );
};

export default MismatchedSoundRecordingsAudit;
